import { inject, Injectable } from '@angular/core';
import { NotificationService } from '../notification/notification.service';
import { Observable, of, throwError } from 'rxjs';
import { ERROR_REASONS, HTTP_STATUS_CODES, isSessionEndedResponse } from '../constants/errors';
import { Location } from '@angular/common';
import { AppService } from '../../app.service';
import { ModalContentService } from '../modal-content/modal-content.service';
import { ModalContentTypes } from '../constants/modal-content-types';
import { Router } from '@angular/router';
import { LocalStorageService } from '../local-storage.service';
import { LOCALSTORAGE_KEYS } from '../constants/databases';
import { Session } from '../types/session.type';

@Injectable({
  providedIn: 'root',
})
export class ErrorHandlerService {

  private isSessionExpired: boolean = false;
  private preventMessage: boolean = false;
  private location = inject(Location);
  private appService = inject(AppService);
  private modalContentService = inject(ModalContentService);
  #router = inject(Router);
  #localStorageService = inject(LocalStorageService);

  constructor(private notificationService: NotificationService) { }

  handleHttpError(
    error: any,
    returnNull: boolean = true,
    showErrorMessage: boolean = true,
    returnError: boolean = false
  ): Observable<any> {
    this.preventMessage = false;
    if (this.isSessionExpired) return of(null);

    let httpError = '';
    switch (error.status) {
      case HTTP_STATUS_CODES.NOT_FOUND:
        httpError = 'Not Found';
        break;
      case HTTP_STATUS_CODES.TOO_MANY_REQUESTS:
        httpError = 'Too Many Requests';
        break;
      case HTTP_STATUS_CODES.SERVER_ERROR:
        this.appService.getMaintenanceState(true);
        this.preventMessage = true;
        break;
      case HTTP_STATUS_CODES.UNKNOWN_ERROR:
        this.preventMessage = true;
        break;
      case HTTP_STATUS_CODES.I_AM_A_TEAPOT:
        if (window?.location)
          this.location.replaceState('/');
        window.location.reload();
        break;
      case HTTP_STATUS_CODES.SUSPENDE_USER:
        httpError = 'Suspended user';
        this.preventMessage = true;
        break;
      case HTTP_STATUS_CODES.UNAUTHORIZED:
      case HTTP_STATUS_CODES.SESSION_EXPIRED:
        // The backstop for an explorer who got past the client-side gates
        // (DEV-11262). The server's own wording names an endpoint, which is not
        // something to put in front of a customer, so it is swapped for the
        // step that actually unblocks them.
        if (error?.error?.reason === ERROR_REASONS.EXPLORER_RESTRICTED) {
          this.preventMessage = true;
          this.explorerRestrictedError();
          break;
        }
        // Neither 401 nor 403 means "signed out" on its own: both are also used for
        // business and validation failures. Only an explicit reason settles it.
        if (!isSessionEndedResponse(error.status, error?.error?.reason)) break;
        this.isSessionExpired = true;
        this.preventMessage = true;
        setTimeout(() => {
          localStorage.clear();
          const cookiesToDelete = ['__kla_id'];
          cookiesToDelete.forEach(cookie => {
            document.cookie = `${cookie}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
          });
          this.location.replaceState('/');
          window.location.reload();
        }, 1000);
        break;
      default:
        break;
    }

    const unknownError = 'Service unavailable error';
    const errorMessagesFromResponse = error?.error?.message ? (showErrorMessage ? error?.error?.message : -1) : httpError || null;

    if (errorMessagesFromResponse === -1) return returnNull ? of(null) : returnError ? of({ data: error.error.data ?? null, message: error.error.message || 'Internal Error', error: true, status: error.status || 0, reason: error.error.reason }) : throwError(() => error);

    let text: any = errorMessagesFromResponse || unknownError
    if (!this.isSessionExpired && !this.preventMessage)
      this.notificationService.show({ text, type: 'error' });

    return returnNull ? of(null) : returnError ? of({ data: error.error.data ?? null, message: error.error.message || 'Internal Error', error: true, status: error.status || 0, reason: error.error.reason }) : throwError(() => error);
  }

  /**
   * Both explorer states land on /signup, because that is where each one's next
   * step lives: an anonymous explorer starts at the beginning, and an activated
   * one is sent straight to the payment step by the stepper's own
   * `hasSession && !hasPaymentMethod` rule. Only the wording differs, because
   * what they still owe us differs.
   */
  explorerRestrictedError() {
    const session: Session | null = this.#localStorageService.get(LOCALSTORAGE_KEYS.SESSION);
    const needsAccount = !!session?.settings?.isAnonymousExplorer;

    this.modalContentService.openModal(ModalContentTypes.CONFIRMATION, {
      closeable: true,
      title: needsAccount ? 'Complete Your Signup' : 'Add a Payment Method',
      textContent: needsAccount
        ? `
        <p>Your items are saved. Finish creating your account and add a payment method to
        place this order &mdash; nothing is charged until you do.</p>
        `
        : `
        <p>Your items are saved. Add a payment method to place this order &mdash; nothing is
        charged until your delivery.</p>
        `,
      confirmButtonText: needsAccount ? 'Complete Signup' : 'Add Payment Method',
      cancelButtonText: 'Keep Shopping'
    }).closed.subscribe((result: { confirm: boolean } | null) => {
      if (result?.confirm) this.#router.navigate(['/signup']);
    });
  }
}
